import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { motion, useInView } from "framer-motion";
import heroImage from "@/assets/images/hero-piano.webp";

const HERO_IMAGE = heroImage;

/* Floating dust */
function DustParticles({ count = 30, isMobile }) {
  const particles = useMemo(
    () =>
      Array.from({ length: count }).map(() => ({
        left: Math.random() * 100,
        size: isMobile ? 1.5 + Math.random() * 1.5 : 1 + Math.random() * 3,
        duration: 12 + Math.random() * 18,
        delay: Math.random() * 0.01,
        drift: (Math.random() - 0.5) * 80,
      })),
    [count, isMobile]
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p, i) => (
        <span
          key={i}
          className="dust"
          style={{
            left: `${p.left}%`,
            bottom: "-10px",
            width: p.size,
            height: p.size,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            "--drift": `${p.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

/* Floating music notes (decorative) */
function FloatingNotes({ isMobile }) {
  const desktopNotes = [
    { x: 8, y: 18, d: 7, delay: 0, rot: -10, opacity: 0.55 },
    { x: 92, y: 12, d: 8, delay: 1.2, rot: 12, opacity: 0.5 },
    { x: 16, y: 36, d: 9, delay: 2, rot: 6, opacity: 0.4 },
    { x: 86, y: 40, d: 7.5, delay: 0.6, rot: -8, opacity: 0.45 },
    { x: 5, y: 58, d: 8.5, delay: 1.8, rot: 14, opacity: 0.35 },
    { x: 95, y: 64, d: 7, delay: 0.3, rot: -14, opacity: 0.4 },
  ];

  const mobileNotes = [
    { x: 8, y: 18, d: 7, delay: 0, rot: -10, opacity: 0.55 },
    { x: 92, y: 12, d: 8, delay: 1.2, rot: 12, opacity: 0.5 },
    { x: 50, y: 40, d: 8, delay: 0.6, rot: 0, opacity: 0.45 },
  ];

  const notes = isMobile ? mobileNotes : desktopNotes;

  return (
    <>
      {notes.map((n, i) => (
        <motion.div
          key={i}
          className="absolute pointer-events-none"
          style={{ left: `${n.x}%`, top: `${n.y}%`, rotate: n.rot, opacity: n.opacity }}
          animate={{ y: [0, -22, 0, -12, 0] }}
          transition={{
            duration: n.d,
            repeat: Infinity,
            delay: n.delay,
            ease: "easeInOut",
          }}
        >
          <svg width="32" height="42" viewBox="0 0 34 44" fill="none">
            <ellipse cx="11" cy="33" rx="9" ry="7" fill="#E57324" />
            <rect x="18" y="6" width="2.4" height="28" fill="#E57324" />
            <path
              d="M20.4 6 C 28 10, 30 16, 26 22"
              stroke="#E57324"
              strokeWidth="2.4"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        </motion.div>
      ))}
    </>
  );
}

export default function PianoScene() {
  const [isMobile, setIsMobile] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const sectionRef = useRef(null);
  const imgRef = useRef(null);
  const isInView = useInView(sectionRef, { amount: 0.1 });

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Detectar cuando la imagen de fondo se ha cargado
  useEffect(() => {
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.src = HERO_IMAGE;
  }, []);

  const particlesCount = isMobile ? 15 : 30;

  return (
    <div
      ref={sectionRef}
      className="absolute inset-0 overflow-hidden"
      style={{ background: "#050303" }}
    >
      {/* Placeholder oscuro mientras carga la imagen */}
      <div
        className={`absolute inset-0 transition-opacity duration-700 ${
          imageLoaded ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        style={{ background: "#050303" }}
      >
        <div className="w-full h-full animate-pulse bg-gradient-to-br from-[#1A1512] to-[#050303]" />
      </div>

      {/* Base photo with subtle Ken Burns — fit by height so top (raised hand) and bottom (candles) stay visible */}
      <motion.div
        ref={imgRef}
        className="absolute inset-0 hero-photo"
        initial={{ scale: isMobile ? 1.0 : 1.0, opacity: 0 }}
        animate={{
          scale: isInView ? (isMobile ? 1.02 : 1.04) : (isMobile ? 1.0 : 1.0),
          opacity: imageLoaded ? 1 : 0,
        }}
        transition={{
          scale: {
            duration: 22,
            ease: "easeOut",
            repeat: isInView ? Infinity : 0,
            repeatType: "reverse",
          },
          opacity: { duration: 1.2, ease: "easeOut" },
        }}
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "auto 100%",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#050303",
          filter: "brightness(0.85) contrast(1.05) saturate(1.05)",
        }}
      />

      {/* Warm candle ambient glow */}
      <motion.div
        className="absolute inset-0 pointer-events-none mix-blend-soft-light"
        animate={isInView ? { opacity: [0.7, 1, 0.85, 1, 0.78] } : { opacity: 0.85 }}
        transition={{
          duration: 4.5,
          repeat: isInView ? Infinity : 0,
          ease: "easeInOut",
        }}
        style={{
          background:
            "radial-gradient(ellipse at 65% 55%, rgba(255,160,60,0.45) 0%, rgba(229,115,36,0.2) 30%, rgba(0,0,0,0) 70%)",
        }}
      />

      {/* Floating notes (decorative) */}
      <FloatingNotes isMobile={isMobile} />

      {/* Dust particles */}
      <DustParticles count={particlesCount} isMobile={isMobile} />

      {/* Side fades to blend image edges with dark theme */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, #050303 0%, rgba(5,3,3,0) 18%, rgba(5,3,3,0) 82%, #050303 100%)",
        }}
      />

      {/* RIGHT-side darkening gradient for text legibility (over piano body) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(260deg, rgba(5,3,3,0.95) 0%, rgba(5,3,3,0.82) 22%, rgba(5,3,3,0.45) 42%, rgba(5,3,3,0.05) 60%, rgba(5,3,3,0) 70%)",
        }}
      />

      {/* Bottom-right intensifier for CTA legibility */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 78% 78%, rgba(5,3,3,0.78) 0%, rgba(5,3,3,0) 45%)",
        }}
      />

      {/* Bottom darkening + Vignette (fused) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(5,3,3,0.5) 0%, rgba(5,3,3,0) 22%, rgba(5,3,3,0) 60%, rgba(5,3,3,0.85) 100%), radial-gradient(ellipse at center, transparent 40%, rgba(5,3,3,0.7) 100%)",
        }}
      />
    </div>
  );
}
